<template>
  <div class="overflow-hidden">
    <div class="container">
      <div class="row section-header">
        <div class="col">
          <div class="title-blue">{{ t("FAMILY_MODULE") }}</div>
        </div>
      </div>
      <div class="row section">
        <div class="col-12">
          <text-block content-id="introBlock" v-on:save="save" :content="content.elements.introBlock"/>
        </div>
      </div>
    </div>

    <div class="section" id="audience">
      <div class="container">
        <div class="row">
          <div class="col-6">
            <text-block content-id="infoBlock1" v-on:save="save" :content="content.elements.infoBlock1"/>
          </div>
          <div class="col-6">
            <text-block content-id="infoBlock2" v-on:save="save" :content="content.elements.infoBlock2"/>
          </div>
        </div>
      </div>
    </div>

    <div class="container section">
      <div class="row py-3 public-module-collapse-row" :class="{ collapsed: !benefitsOpen }" @click="benefitsOpen = !benefitsOpen">
        <div class="col-auto">
          <div class="text24-blue public-module-collapse-title">{{ t("BENEFITS") }}</div>
        </div>
        <div class="col-1 ms-auto text-end">
          <i class="pi pi-chevron-down public-module-chevron closed"/>
          <i class="pi pi-chevron-up public-module-chevron opened"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-show="benefitsOpen" class="mt-4">
            <text-and-picture :data="benefitsBlock" :picture-left="true"/>
          </div>
        </div>
      </div>

      <div class="row py-3 public-module-collapse-row" :class="{ collapsed: !methodsOpen }" @click="methodsOpen = !methodsOpen">
        <div class="col-auto">
          <div class="text24-blue public-module-collapse-title">{{ t("TRAINING_METHODS") }}</div>
        </div>
        <div class="col-1 ms-auto text-end">
          <i class="pi pi-chevron-down public-module-chevron closed"/>
          <i class="pi pi-chevron-up public-module-chevron opened"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-show="methodsOpen" class="mt-4">
            <text-and-picture :data="methodsBlock"/>
          </div>
        </div>
      </div>

      <div class="row py-3 public-module-collapse-row" :class="{ collapsed: !structureOpen }" @click="structureOpen = !structureOpen">
        <div class="col-auto">
          <div class="text24-blue public-module-collapse-title">{{ t("STRUCT") }}</div>
        </div>
        <div class="col-1 ms-auto text-end">
          <i class="pi pi-chevron-down public-module-chevron closed"/>
          <i class="pi pi-chevron-up public-module-chevron opened"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-show="structureOpen" class="mt-4">
            <text-block content-id="structureBlock" v-on:save="save" :content="content.elements.structureBlock"/>
          </div>
        </div>
      </div>
    </div>

    <div class="section public-module-purple-bg">
      <div class="container">
        <div class="row">
          <div class="col-6">
            <text-and-picture :data="curatorsBlock1" :picture-left="true"/>
          </div>
          <div class="col-6">
            <text-and-picture :data="curatorsBlock2" :picture-left="true"/>
            <LogosContainer/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import EventBus from "@/util/eventBus";
import {usePageContent} from "@/mixins/pageContentMixin2";

import TextBlock from "@/views/components/TextBlock.vue";
import TextAndPicture from "@/views/components/TextAndPicture.vue";
import training from "@/assets/training.png";
import training_bg from "@/assets/training_bg.svg";
import icon_cert from "@/assets/icon/icon_cert.svg";
import men from "@/assets/men.png";
import men_bg from "@/assets/men_bg.svg";
import LogosContainer from "@/views/public/LogosContainer.vue";

const {getPageContent, savePageContent} = usePageContent();
const axios = injectAxios();
const {t, locale} = useI18n();
const content = ref({elements: {}});
const pageName = "familyModule";
const benefitsOpen = ref(true);
const methodsOpen = ref(true);
const structureOpen = ref(true);

onMounted(() => {
  EventBus.$on("locale", loadPageContent);
  loadPageContent();
});

onBeforeUnmount(() => {
  EventBus.$off("locale", loadPageContent);
});

async function loadPageContent() {
  content.value = await getPageContent(axios, pageName, locale.value);
}

const save = (elem) => {
  savePageContent(axios, pageName, locale.value, elem.element, elem.value);
}

const benefitsBlock = computed(() => {
  return {
    page: pageName,
    element: "benefitsBlock",
    image: {
      src: men,
      style: "width: 360px",
      mask: {
        src: men_bg,
        style: "position: absolute; right: -387px; top: -19px;",
      },
    },
    text1: content.value ? content.value.elements.benefitsBlockText1 : "",
    text2: content.value ? content.value.elements.benefitsBlockText2 : "",
  };
});

const methodsBlock = computed(() => {
  return {
    page: pageName,
    element: "methodsBlock",
    image: {
      src: training,
      style: "width: 360px",
      mask: {
        src: training_bg,
        style: "position: absolute; right: -311px; top: -22px;",
      },
    },
    text1: content.value ? content.value.elements.methodsBlockText1 : "",
    text2: content.value ? content.value.elements.methodsBlockText2 : "",
  };
});

const curatorsBlock1 = computed(() => {
  return {
    page: pageName,
    element: "curatorsBlock1",
    image: {src: icon_cert},
    text1: content.value ? content.value.elements.curatorsBlock1Text1 : "",
    text2: content.value ? content.value.elements.curatorsBlock1Text2 : "",
  };
});

const curatorsBlock2 = computed(() => {
  return {
    page: pageName,
    element: "curatorsBlock2",
    image: {src: icon_cert},
    text1: content.value ? content.value.elements.curatorsBlock2Text1 : "",
    text2: content.value ? content.value.elements.curatorsBlock2Text2 : "",
  };
});
</script>

<style scoped lang="scss">
.public-module-collapse-title { font-size: 32px; }
.public-module-collapse-row { cursor: pointer; }
.public-module-chevron { color: blue; }
.public-module-collapse-row:not(.collapsed) .public-module-chevron.closed,
.public-module-collapse-row.collapsed .public-module-chevron.opened { display: none; }
.public-module-collapse-row.collapsed .public-module-chevron.closed,
.public-module-collapse-row:not(.collapsed) .public-module-chevron.opened { display: inline; }
.public-module-purple-bg { background-color: #d2c3d4; background-size: cover; }
#audience {
  background: url("@/assets/audience_gradience.jpg");
  background-size: cover;
}
</style>
